import {
  clonePreset,
  DEFAULT_PRESETS,
  DEFAULT_SLOT_NAMES,
  CLASSICAL_PRESETS,
  ELECTRONIC_PRESETS,
  KEYGEN_PRESETS,
  MODULATION_PRESETS,
  type SynthPreset,
} from "../../../../src/lib/audio";

/** Fixed library shelves — calm, short labels for filters and group headers. */
export const PRESET_CATEGORIES = [
  { id: "core", label: "Core" },
  { id: "keygen", label: "Keygen" },
  { id: "electronic", label: "Electronic" },
  { id: "classical", label: "Classical" },
  { id: "modulation", label: "Modulation" },
  { id: "user", label: "User" },
] as const;

export type PresetCategoryId = typeof PRESET_CATEGORIES[number]["id"];

export type NamedPreset = {
  id: string;
  name: string;
  /** Factory entries — cannot be deleted (reset instead). */
  builtin?: boolean;
  /** Primary shelf for grouping / filtering. */
  category: PresetCategoryId;
  /** Freeform search labels; optional. */
  tags?: string[];
  preset: SynthPreset;
};

/** Stable ids for the 8 default slot instruments. */
export const SLOT_BUILTIN_IDS = [
  "builtin-bass",
  "builtin-lead",
  "builtin-kick",
  "builtin-hat",
  "builtin-pad",
  "builtin-arp",
  "builtin-snare",
  "builtin-strings",
] as const;

/** Soft keygen / cracktro pack — bundled after the slot defaults. */
export const KEYGEN_BUILTIN_IDS = [
  "builtin-keygen-sub-root",
  "builtin-keygen-warm-pluck",
  "builtin-keygen-pulse-lead",
  "builtin-keygen-chirp-arp",
  "builtin-keygen-glass-bell",
  "builtin-keygen-nostalgia-pad",
  "builtin-keygen-airy-keys",
  "builtin-keygen-soft-kick",
  "builtin-keygen-paper-snare",
  "builtin-keygen-tick-hat",
] as const;

/** Classic electronic pack — bundled after the keygen presets. */
export const ELECTRONIC_BUILTIN_IDS = [
  "builtin-elec-iceberg-lead",
  "builtin-elec-acid-serpent",
  "builtin-elec-reese-undertow",
  "builtin-elec-warehouse-stab",
  "builtin-elec-hoover-riot",
  "builtin-elec-neon-rain-pluck",
  "builtin-elec-basement-organ",
  "builtin-elec-aurora-drift",
  "builtin-elec-dub-sonar",
  "builtin-elec-cavern-tom",
] as const;

/** Classical / Baroque pack — bundled after the electronic presets. */
export const CLASSICAL_BUILTIN_IDS = [
  "builtin-class-silver-traverso",
  "builtin-class-candlelight-clavier",
  "builtin-class-chapel-positif",
  "builtin-class-baroque-fiddle",
  "builtin-class-amber-cello",
  "builtin-class-nasal-hautbois",
  "builtin-class-crystal-fortepiano",
  "builtin-class-celestial-chorale",
  "builtin-class-soft-choir",
  "builtin-class-gilded-clarion",
  "builtin-class-ivory-music-box",
] as const;

/** Osc3 / FM / ring / noise pack — bundled after the classical presets. */
export const MODULATION_BUILTIN_IDS = [
  "builtin-mod-chrome-supersaw",
  "builtin-mod-glass-carillon",
  "builtin-mod-temple-bell",
  "builtin-mod-ivory-operator",
  "builtin-mod-wire-harp",
  "builtin-mod-submarine-sonar",
  "builtin-mod-iron-lattice",
  "builtin-mod-phase-dagger",
  "builtin-mod-ring-spire",
  "builtin-mod-chip-bell",
  "builtin-mod-tracker-choir",
  "builtin-mod-void-reese",
  "builtin-mod-rusted-gate",
  "builtin-mod-orbital-brass",
  "builtin-mod-paper-crane",
  "builtin-mod-crystal-shard",
  "builtin-mod-neon-abyss",
  "builtin-mod-cavern-tom-fm",
  "builtin-mod-soft-operator",
  "builtin-mod-deep-well",
] as const;

/** @deprecated Prefer SLOT_BUILTIN_IDS for slot mapping; includes factory packs. */
export const BUILTIN_IDS = [
  ...SLOT_BUILTIN_IDS,
  ...KEYGEN_BUILTIN_IDS,
  ...ELECTRONIC_BUILTIN_IDS,
  ...CLASSICAL_BUILTIN_IDS,
  ...MODULATION_BUILTIN_IDS,
] as const;

const LEGACY_SLOT_IDS: Record<string, (typeof SLOT_BUILTIN_IDS)[number]> = {
  "builtin-inst5": "builtin-pad",
  "builtin-inst6": "builtin-arp",
  "builtin-inst7": "builtin-snare",
  "builtin-inst8": "builtin-strings",
};

const LEGACY_SLOT_NAMES: Record<string, string> = {
  "Inst 5": "Pad",
  "Inst 6": "Arp",
  "Inst 7": "Snare",
  "Inst 8": "Strings",
};

const CATEGORY_IDS = new Set<string>(PRESET_CATEGORIES.map(entry => entry.id));

/** Role tags for the eight core slot instruments. */
const CORE_TAGS: readonly (readonly string[])[] = [
  ["bass"],
  ["lead"],
  ["drums", "kick"],
  ["drums", "hat"],
  ["pad"],
  ["arp"],
  ["drums", "snare"],
  ["pad", "strings"],
];

/** Role tags for the keygen / cracktro pack (order matches KEYGEN_PRESETS). */
const KEYGEN_TAGS: readonly (readonly string[])[] = [
  ["bass", "sub"],
  ["bass", "pluck"],
  ["lead"],
  ["arp"],
  ["bell", "keys"],
  ["pad"],
  ["keys"],
  ["drums", "kick"],
  ["drums", "snare"],
  ["drums", "hat"],
];

/** Role tags for the electronic pack (order matches ELECTRONIC_PRESETS). */
const ELECTRONIC_TAGS: readonly (readonly string[])[] = [
  ["lead"],
  ["bass", "acid"],
  ["bass", "reese"],
  ["stab", "keys"],
  ["lead", "hoover"],
  ["pluck", "arp"],
  ["organ", "keys"],
  ["pad"],
  ["perc", "fx"],
  ["drums", "tom"],
];

/** Role tags for the classical pack (order matches CLASSICAL_PRESETS). */
const CLASSICAL_TAGS: readonly (readonly string[])[] = [
  ["woodwind", "flute", "lead"],
  ["keys", "pluck"],
  ["organ", "keys"],
  ["strings", "lead"],
  ["strings", "bass"],
  ["woodwind", "reed", "lead"],
  ["keys", "piano"],
  ["pad", "choir"],
  ["pad", "choir"],
  ["brass", "lead"],
  ["bell", "keys"],
];

/** Role tags for the modulation / FM pack (order matches MODULATION_PRESETS). */
const MODULATION_TAGS: readonly (readonly string[])[] = [
  ["lead", "fm"],
  ["bell", "keys", "fm"],
  ["bell", "fm"],
  ["keys", "piano", "fm"],
  ["pluck", "harp", "fm"],
  ["bass", "fm"],
  ["pad", "fm"],
  ["lead", "fm"],
  ["lead", "fm"],
  ["bell", "arp", "fm"],
  ["pad", "choir", "fm"],
  ["bass", "reese", "fm"],
  ["drums", "perc", "fm"],
  ["brass", "lead", "fm"],
  ["pad", "fm"],
  ["pluck", "keys", "fm"],
  ["pad", "fm"],
  ["drums", "tom", "fm"],
  ["pad", "keys", "fm"],
  ["bass", "fm"],
];

export function isPresetCategoryId(value: unknown): value is PresetCategoryId {
  return typeof value === "string" && CATEGORY_IDS.has(value);
}

export function sanitizePresetCategory(
  value: unknown,
  fallback: PresetCategoryId = "user",
): PresetCategoryId {
  return isPresetCategoryId(value) ? value : fallback;
}

export function sanitizePresetTags(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const tags = value
    .filter((tag): tag is string => typeof tag === "string")
    .map(tag => tag.trim().toLowerCase())
    .filter(Boolean)
    .slice(0, 12);
  return tags.length > 0 ? [...new Set(tags)] : undefined;
}

export function categoryLabel(id: PresetCategoryId): string {
  return PRESET_CATEGORIES.find(entry => entry.id === id)?.label ?? id;
}

export function categorySortIndex(id: PresetCategoryId): number {
  const index = PRESET_CATEGORIES.findIndex(entry => entry.id === id);
  return index >= 0 ? index : PRESET_CATEGORIES.length;
}

function named(
  id: string,
  name: string,
  category: PresetCategoryId,
  preset: SynthPreset,
  tags?: readonly string[],
): NamedPreset {
  return {
    id,
    name,
    builtin: true,
    category,
    ...(tags && tags.length > 0 ? { tags: [...tags] } : {}),
    preset: clonePreset(preset),
  };
}

export function createDefaultBank(): NamedPreset[] {
  const slots = DEFAULT_PRESETS.map((preset, i) =>
    named(
      SLOT_BUILTIN_IDS[i]!,
      preset.name || DEFAULT_SLOT_NAMES[i]!,
      "core",
      preset,
      CORE_TAGS[i],
    ),
  );
  const keygen = KEYGEN_PRESETS.map((preset, i) =>
    named(KEYGEN_BUILTIN_IDS[i]!, preset.name, "keygen", preset, KEYGEN_TAGS[i]),
  );
  const electronic = ELECTRONIC_PRESETS.map((preset, i) =>
    named(ELECTRONIC_BUILTIN_IDS[i]!, preset.name, "electronic", preset, ELECTRONIC_TAGS[i]),
  );
  const classical = CLASSICAL_PRESETS.map((preset, i) =>
    named(CLASSICAL_BUILTIN_IDS[i]!, preset.name, "classical", preset, CLASSICAL_TAGS[i]),
  );
  const modulation = MODULATION_PRESETS.map((preset, i) =>
    named(MODULATION_BUILTIN_IDS[i]!, preset.name, "modulation", preset, MODULATION_TAGS[i]),
  );
  return [...slots, ...keygen, ...electronic, ...classical, ...modulation];
}

export function newPresetId(): string {
  return `user-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

function normalizeBankId(id: string): string {
  return LEGACY_SLOT_IDS[id] ?? id;
}

export function sanitizeBank(raw: unknown): NamedPreset[] {
  const factory = createDefaultBank();
  if (!Array.isArray(raw) || raw.length === 0) {
    return factory;
  }

  const byId = new Map<string, NamedPreset>();
  const categoryOverrides = new Map<string, PresetCategoryId>();
  const tagOverrides = new Map<string, string[] | null>();
  for (const item of raw) {
    if (!item || typeof item !== "object") {
      continue;
    }
    const entry = item as Partial<NamedPreset>;
    if (typeof entry.id !== "string" || typeof entry.name !== "string" || !entry.preset) {
      continue;
    }
    const preset = entry.preset as SynthPreset;
    if (!preset.osc1 || !preset.filter || !preset.ampEnv) {
      continue;
    }
    const id = normalizeBankId(entry.id);
    const rawName = entry.name.trim() || "Untitled";
    const name = LEGACY_SLOT_NAMES[rawName] ?? rawName;
    const builtin = Boolean(entry.builtin) || (BUILTIN_IDS as readonly string[]).includes(id);
    if (isPresetCategoryId(entry.category)) {
      categoryOverrides.set(id, entry.category);
    }
    if ("tags" in entry) {
      tagOverrides.set(id, sanitizePresetTags(entry.tags) ?? null);
    }
    const tags = sanitizePresetTags(entry.tags);
    byId.set(id, {
      id,
      name,
      builtin,
      category: categoryOverrides.get(id) ?? (builtin ? "core" : "user"),
      ...(tags ? { tags } : {}),
      preset: clonePreset({ ...preset, name: name === "Untitled" ? preset.name : name }),
    });
  }

  // Ensure factory builtins exist; keep user edits (including category reassignment) by id.
  const bank: NamedPreset[] = [];
  for (const factoryEntry of factory) {
    const existing = byId.get(factoryEntry.id);
    if (existing) {
      const merged: NamedPreset = {
        ...factoryEntry,
        name: existing.name,
        preset: existing.preset,
        builtin: true,
        id: factoryEntry.id,
        category: categoryOverrides.get(factoryEntry.id) ?? factoryEntry.category,
      };
      if (tagOverrides.has(factoryEntry.id)) {
        const tags = tagOverrides.get(factoryEntry.id);
        if (tags && tags.length > 0) {
          merged.tags = tags;
        } else {
          delete merged.tags;
        }
      } else if (factoryEntry.tags) {
        merged.tags = factoryEntry.tags;
      }
      bank.push(merged);
      byId.delete(factoryEntry.id);
    } else {
      bank.push(factoryEntry);
    }
  }
  for (const rest of byId.values()) {
    if (!rest.builtin) {
      bank.push({
        ...rest,
        builtin: false,
        category: categoryOverrides.get(rest.id) ?? "user",
      });
    }
  }
  return bank;
}

/**
 * Import / open project: file bank is base; keep non-builtin session presets
 * the file doesn't include (by id). Imported id wins on collision.
 */
export function mergeBanksPreservingUser(
  imported: readonly NamedPreset[] | undefined,
  existing: readonly NamedPreset[] | undefined,
): NamedPreset[] {
  const base = sanitizeBank(imported ?? createDefaultBank());
  if (!existing?.length) {
    return base;
  }
  const ids = new Set(base.map(entry => entry.id));
  const out = [...base];
  for (const entry of existing) {
    if (entry.builtin || ids.has(entry.id)) {
      continue;
    }
    out.push({
      ...entry,
      builtin: false,
      category: entry.category || "user",
      preset: clonePreset(entry.preset),
      ...(entry.tags ? { tags: [...entry.tags] } : {}),
    });
    ids.add(entry.id);
  }
  return out;
}

export function isSynthPreset(value: unknown): value is SynthPreset {
  if (!value || typeof value !== "object") {
    return false;
  }
  const preset = value as SynthPreset;
  return Boolean(preset.osc1 && preset.osc2 && preset.filter && preset.ampEnv);
}
